import React, { useState, useCallback, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Slider,
    Button,
    CircularProgress,
    LinearProgress,
    IconButton
} from "@mui/material";
import { CheckCircle, ArrowBack, ArrowForward } from "@mui/icons-material";
import Cropper from 'react-easy-crop';

const CropModal = ({
    open,
    onClose,
    croppingQueue = [],
    currentIndex = 0,
    onNavigate,
    onSaveCurrentCrop,
    onFinish,
    loading = false,
    aspectRatio = 800 / 950
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    useEffect(() => {
        if (open && croppingQueue[currentIndex]) {
            const current = croppingQueue[currentIndex];
            if (current.cropState) {
                setCrop(current.cropState.crop);
                setZoom(current.cropState.zoom);
            } else {
                setCrop({ x: 0, y: 0 });
                setZoom(1);
            }
        }
    }, [currentIndex, open, croppingQueue]);

    const handleCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleNext = useCallback(async () => {
        if (!croppedAreaPixels) return;
        await onSaveCurrentCrop(currentIndex, croppedAreaPixels, { crop, zoom });
        if (currentIndex < croppingQueue.length - 1) {
            onNavigate(currentIndex + 1);
        }
    }, [croppedAreaPixels, currentIndex, crop, zoom, onSaveCurrentCrop, onNavigate, croppingQueue.length]);

    const handlePrevious = useCallback(() => {
        if (croppedAreaPixels) {
            onSaveCurrentCrop(currentIndex, croppedAreaPixels, { crop, zoom });
        }
        if (currentIndex > 0) {
            onNavigate(currentIndex - 1);
        }
    }, [croppedAreaPixels, currentIndex, crop, zoom, onSaveCurrentCrop, onNavigate]);

    const handleFinish = useCallback(async () => {
        if (croppedAreaPixels) {
            await onSaveCurrentCrop(currentIndex, croppedAreaPixels, { crop, zoom });
        }
        onFinish();
    }, [croppedAreaPixels, currentIndex, crop, zoom, onSaveCurrentCrop, onFinish]);

    const currentImage = croppingQueue[currentIndex];
    if (!currentImage || !open) {
        return null;
    }

    const isLastImage = currentIndex === croppingQueue.length - 1;
    const isFirstImage = currentIndex === 0;
    const progress = currentIndex + 1;
    const total = croppingQueue.length;
    const progressPercent = (progress / total) * 100;

    return (
        <Dialog
            open={open}
            onClose={() => { /* Prevent accidental close */ }}
            fullWidth
            maxWidth="md"
            disableEscapeKeyDown={loading}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Crop Image {progress}/{total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {currentImage.file?.name || 'Image'}
                    </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progressPercent} sx={{ mt: 1 }} />
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: 400,
                    background: '#333',
                    borderRadius: 1
                }}>
                    <Cropper
                        image={currentImage.url}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspectRatio}
                        onCropChange={setCrop}
                        onCropComplete={handleCropComplete}
                        onZoomChange={setZoom}
                        showGrid={true}
                        restrictPosition={false}
                    />
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.2)',
                        borderRadius: 4,
                    }
                }}>
                    {croppingQueue.map((img, idx) => (
                        <Box
                            key={`preview-${idx}`}
                            onClick={() => {
                                if (croppedAreaPixels) {
                                    onSaveCurrentCrop(currentIndex, croppedAreaPixels, { crop, zoom });
                                }
                                onNavigate(idx);
                            }}
                            sx={{
                                position: 'relative',
                                minWidth: 60,
                                width: 60,
                                height: 60,
                                border: idx === currentIndex ? '3px solid #1976d2' : '2px solid #ddd',
                                borderRadius: 1,
                                opacity: img.cropped ? 0.7 : 1,
                                overflow: 'hidden',
                                flexShrink: 0,
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: '#1976d2',
                                    opacity: 1
                                }
                            }}
                        >
                            <Box
                                component="img"
                                src={img.url}
                                alt={`preview-${idx}`}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            {img.cropped && (
                                <CheckCircle
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        color: '#4caf50',
                                        bgcolor: 'white',
                                        borderRadius: '50%',
                                        fontSize: 20
                                    }}
                                />
                            )}
                            {idx === currentIndex && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        fontSize: 10,
                                        textAlign: 'center',
                                        py: 0.5
                                    }}
                                >
                                    Current
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="overline" gutterBottom>
                        Zoom Level: {zoom.toFixed(1)}x
                    </Typography>
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        aria-labelledby="zoom-slider"
                        onChange={(e, newZoom) => setZoom(newZoom)}
                        sx={{ width: '80%' }}
                        valueLabelDisplay="auto"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton onClick={handlePrevious} disabled={isFirstImage || loading} color="primary">
                        <ArrowBack />
                    </IconButton>
                    <IconButton onClick={handleNext} disabled={isLastImage || loading || !croppedAreaPixels} color="primary">
                        <ArrowForward />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={() => { onClose(); }}
                        color="error"
                        variant="outlined"
                        sx={{ color: "#d32f2f" }}
                    >
                        Cancel All
                    </Button>
                    {!isLastImage ? (
                        <Button
                            onClick={handleNext}
                            color="success"
                            variant="contained"
                            disabled={loading || !croppedAreaPixels}
                        >
                            {loading ? <CircularProgress size={24} /> : `Next (${progress}/${total})`}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleFinish}
                            color="success"
                            variant="contained"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : "Finish & Save"}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('No 2d ctx');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (!blob) reject(new Error('Canvas is empty'));
            const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
            resolve(file);
        }, 'image/jpeg', 0.95);
    });
};

export { getCroppedImg };
export default CropModal;
